import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { List, X, Phone } from "@phosphor-icons/react";
import { PHONE, PHONE_TEL } from "@/lib/siteData";

const NAV = [
  { label: "Services", href: "#services" },
  { label: "Gallery", href: "#gallery" },
  { label: "About", href: "#team" },
  { label: "Service Area", href: "#service-area" },
  { label: "Contact", href: "#contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-ink/80 backdrop-blur-xl border-b border-line" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex items-center justify-between h-20">
          <a href="#top" data-testid="logo-link" className="flex items-center shrink-0">
            <span className="bg-white rounded-md p-1 flex items-center justify-center shadow-sm">
              <img src="/logo-white.png" alt="B3 Contracting LLC logo" className="h-14 sm:h-16 w-auto object-contain" />
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-9">
            {NAV.map((n) => (
              <a
                key={n.label}
                href={n.href}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-sm text-white/70 hover:text-gold transition-colors duration-200 relative group"
              >
                {n.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={`tel:${PHONE_TEL}`}
              data-testid="header-call-button"
              className="hidden sm:inline-flex items-center gap-2 bg-gold text-ink font-semibold text-sm px-5 py-3 hover:bg-gold-hover transition-colors duration-200"
            >
              <Phone weight="fill" size={16} />
              Call {PHONE}
            </a>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
              className="lg:hidden inline-flex items-center justify-center h-11 w-11 border border-line text-white"
            >
              {open ? <X size={22} /> : <List size={22} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-ink/95 backdrop-blur-xl border-b border-line"
          >
            <div className="px-5 py-6 flex flex-col gap-1">
              {NAV.map((n) => (
                <a
                  key={n.label}
                  href={n.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="py-3 text-lg font-heading text-white/90 hover:text-gold transition-colors border-b border-line/50"
                >
                  {n.label}
                </a>
              ))}
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid="mobile-call-button"
                className="mt-4 inline-flex items-center justify-center gap-2 bg-gold text-ink font-semibold px-5 py-4 hover:bg-gold-hover transition-colors"
              >
                <Phone weight="fill" size={18} />
                Call {PHONE}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
